import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getToken } from './api';
import { useAuth } from './auth';
import { aplicarTemaNoRoot, TEMA_PADRAO, type ChaveTema } from './tema';
import {
  TEMPLATES, TEMPLATE_PADRAO_ID, templatePorId, templatePorModo, parDoTemplate,
  aplicarTemplate, aplicarLegado, type ModoTema, type Template,
} from './temas';

type Tema = Partial<Record<ChaveTema, string>>;

// Preferencia de tema POR USUARIO/DISPOSITIVO (localStorage). Guarda o id do
// template ativo ('cobre_claro' | 'cobre_escuro' | 'azul_claro' | 'personalizado').
const PREF_KEY = 'fdc_tema_pref';
// Memoria do ultimo template escolhido EM CADA MODO. Assim o botao sol/lua volta
// pro tema que o usuario tinha naquele modo (ex: estava no Azul claro -> escuro
// -> ao voltar pro claro retorna ao Azul, nao ao Cobre Claro padrao).
const SLOT_CLARO_KEY = 'fdc_tema_claro';
const SLOT_ESCURO_KEY = 'fdc_tema_escuro';
// Versao (atualizado_em ISO) do tema da casa que este aparelho ja honrou.
// Quando o admin troca o tema da loja, atualizado_em muda; no proximo boot
// o aparelho percebe a nova versao e adota o tema da casa mesmo com pref local.
const CASA_VISTO_KEY = 'fdc_tema_casa_visto';

function lerPref(): string | null {
  try { return localStorage.getItem(PREF_KEY); } catch { return null; }
}
function gravarPref(id: string): void {
  try { localStorage.setItem(PREF_KEY, id); } catch { /* noop */ }
}
function lerSlot(modo: ModoTema): string | null {
  try { return localStorage.getItem(modo === 'escuro' ? SLOT_ESCURO_KEY : SLOT_CLARO_KEY); } catch { return null; }
}
function gravarSlot(modo: ModoTema, id: string): void {
  try { localStorage.setItem(modo === 'escuro' ? SLOT_ESCURO_KEY : SLOT_CLARO_KEY, id); } catch { /* noop */ }
}
function lerCasaVisto(): string | null {
  try { return localStorage.getItem(CASA_VISTO_KEY); } catch { return null; }
}
function gravarCasaVisto(ts: string): void {
  try { localStorage.setItem(CASA_VISTO_KEY, ts); } catch { /* noop */ }
}

interface ThemeState {
  /** Cores aplicadas (pro editor avancado). */
  tema: Tema;
  carregado: boolean;
  /** id do template ativo: 'cobre_claro' | 'cobre_escuro' | 'personalizado'. */
  templateAtivoId: string;
  modo: ModoTema;
  /** Lista de templates prontos (pra tela Aparencia). */
  templates: Template[];
  /** Aplica um template e grava como preferencia do usuario (localStorage). */
  escolherTemplate: (id: string) => void;
  /** Alterna claro/escuro (preferencia do usuario). */
  alternarModo: () => void;
  /** Define o padrao da casa (app_settings, admin/gerente). */
  definirPadraoCasa: (id: string) => Promise<void>;
  /**
   * Acao combinada pro admin: aplica o template neste aparelho + define como
   * padrao da loja toda (forcando todos os outros aparelhos no proximo boot) +
   * grava fdc_tema_casa_visto pra o proprio admin nao "re-forcar" no proximo boot.
   */
  definirTemaDaLoja: (id: string) => Promise<void>;
  // ── editor avancado (escreve em /cores = template "personalizado") ──
  atualizar: (parcial: Tema) => Promise<Tema>;
  preview: (parcial: Tema) => void;
  resetPreview: () => void;
}

const ThemeCtx = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(TEMA_PADRAO);
  const [templateAtivoId, setTemplateAtivoId] = useState<string>(TEMPLATE_PADRAO_ID);
  const [modo, setModo] = useState<ModoTema>('claro');
  const [carregado, setCarregado] = useState(false);
  // O tema so e resolvido dentro do `resolver()` abaixo, que depende de estar logado (busca o
  // padrao da casa no banco). O login e navegacao INTERNA (nao recarrega a pagina), entao sem
  // este gancho um aparelho que abriu deslogado (tela de login = Cobre Claro) ficava preso no
  // Cobre Claro apos logar — nunca buscava o padrao da casa. Amarrar ao id do usuario faz o
  // resolver re-rodar na hora do login. (Bug achado no 1o tablet novo, 2026-07-28.)
  const { usuario } = useAuth();

  // Aplica um template built-in e atualiza o estado.
  const aplicarBuiltin = useCallback((t: Template) => {
    aplicarTemplate(t);
    setTema(t.cores);
    setTemplateAtivoId(t.id);
    setModo(t.modo);
  }, []);

  // Carrega o tema "personalizado (legado)" do banco (/cores) e aplica.
  const carregarLegado = useCallback(async () => {
    try {
      const t = await api.get<Tema>('/api/configuracoes/cores');
      const merged = { ...TEMA_PADRAO, ...t } as Record<ChaveTema, string>;
      aplicarLegado(merged);
      setTema(merged);
      setTemplateAtivoId('personalizado');
      setModo('claro');
    } catch {
      aplicarBuiltin(templatePorId(TEMPLATE_PADRAO_ID)!);
    }
  }, [aplicarBuiltin]);

  useEffect(() => {
    let cancelado = false;

    async function resolver() {
      // 1) Deslogado: sem acesso ao banco, usa Cobre Claro (login ja com a marca).
      if (!getToken()) {
        aplicarBuiltin(templatePorId(TEMPLATE_PADRAO_ID)!);
        setCarregado(true);
        return;
      }

      // 2) Logado: busca o tema da casa (valor + atualizado_em).
      let valorCasa: string | null = null;
      let tsNovo: string | null = null;
      try {
        const r = await api.get<{ valor: string | null; atualizado_em?: string | null }>(
          '/api/app-settings/tema_template_padrao',
        );
        valorCasa = r?.valor ?? null;
        tsNovo = r?.atualizado_em ? String(r.atualizado_em) : null;
      } catch {
        /* continua sem casa */
      }

      // 3) Verifica se o tema da casa foi atualizado desde a ultima vez que
      //    este aparelho o honrou. Se sim, forca a adocao — mesmo com pref local.
      const casaVisto = lerCasaVisto();
      const casaAtualizada =
        tsNovo !== null && valorCasa !== null && casaVisto !== tsNovo;

      if (casaAtualizada) {
        const tpl = templatePorId(valorCasa);
        if (tpl) {
          aplicarBuiltin(tpl);
          gravarPref(tpl.id);
          gravarSlot(tpl.modo, tpl.id);
          gravarCasaVisto(tsNovo!);
          if (!cancelado) setCarregado(true);
          return;
        }
        // Se a casa aponta pra "personalizado" ou valor desconhecido, cai no legado
        // e marca como visto pra nao ficar re-forcando.
        if (valorCasa === 'personalizado' && getToken()) {
          gravarCasaVisto(tsNovo!);
          await carregarLegado();
          if (!cancelado) setCarregado(true);
          return;
        }
      }

      // 4) Sem forcagem: preferencia local vence.
      const pref = lerPref();
      if (pref) {
        const tpl = templatePorId(pref);
        if (tpl) { aplicarBuiltin(tpl); if (!cancelado) setCarregado(true); return; }
        if (pref === 'personalizado' && getToken()) { await carregarLegado(); if (!cancelado) setCarregado(true); return; }
      }

      // 5) Sem pref local nem forcagem: usa o padrao da casa normalmente.
      if (valorCasa) {
        const tpl = templatePorId(valorCasa);
        if (tpl) { aplicarBuiltin(tpl); }
        else { await carregarLegado(); }
      } else {
        await carregarLegado();
      }
      if (!cancelado) setCarregado(true);
    }

    void resolver();
    return () => { cancelado = true; };
  }, [aplicarBuiltin, carregarLegado, usuario?.id]);

  const escolherTemplate = useCallback((id: string) => {
    const tpl = templatePorId(id);
    if (tpl) { aplicarBuiltin(tpl); gravarPref(id); gravarSlot(tpl.modo, id); return; }
    if (id === 'personalizado') { gravarPref('personalizado'); gravarSlot('claro', 'personalizado'); void carregarLegado(); }
  }, [aplicarBuiltin, carregarLegado]);

  const alternarModo = useCallback(() => {
    // Lembra o template ATUAL no slot do modo atual, pra poder voltar nele depois.
    gravarSlot(modo, templateAtivoId);
    const proximo: ModoTema = modo === 'escuro' ? 'claro' : 'escuro';
    const lembrado = lerSlot(proximo);
    // "Personalizado (legado)" so existe no modo claro.
    if (proximo === 'claro' && lembrado === 'personalizado') {
      gravarPref('personalizado');
      void carregarLegado();
      return;
    }
    // 1) Template lembrado pro modo destino (escolha explícita anterior).
    const tplLembrado = templatePorId(lembrado);
    if (tplLembrado && tplLembrado.modo === proximo) {
      aplicarBuiltin(tplLembrado);
      gravarPref(tplLembrado.id);
      gravarSlot(proximo, tplLembrado.id);
      return;
    }
    // 2) Par natural do template atual (ex: Azul Claro -> Azul Escuro e vice-versa).
    const tplAtual = templatePorId(templateAtivoId);
    const par = parDoTemplate(tplAtual, proximo);
    if (par) {
      aplicarBuiltin(par);
      gravarPref(par.id);
      gravarSlot(proximo, par.id);
      return;
    }
    // 3) Fallback: template padrao do modo destino (Cobre Claro/Escuro).
    const tpl = templatePorModo(proximo);
    aplicarBuiltin(tpl);
    gravarPref(tpl.id);
    gravarSlot(proximo, tpl.id);
  }, [modo, templateAtivoId, aplicarBuiltin, carregarLegado]);

  const definirPadraoCasa = useCallback(async (id: string) => {
    const result = await api.put<{ valor: string; atualizado_em?: string }>(
      '/api/app-settings/tema_template_padrao',
      { valor: id },
    );
    // Grava o timestamp retornado pelo PUT pra este aparelho nao se "re-forcar"
    // no proximo boot (o admin ja esta com o tema certo).
    const ts = result?.atualizado_em ? String(result.atualizado_em) : null;
    if (ts) gravarCasaVisto(ts);
  }, []);

  /**
   * Acao combinada pro admin: aplica o template neste aparelho, define como
   * padrao da loja toda e grava o timestamp visto (sem re-forcar no proximo boot).
   */
  const definirTemaDaLoja = useCallback(async (id: string) => {
    // 1) Aplica localmente.
    escolherTemplate(id);
    // 2) Grava no banco como padrao da casa.
    await definirPadraoCasa(id);
  }, [escolherTemplate, definirPadraoCasa]);

  // ── editor avancado: grava cores em /cores (= tema "personalizado") ──
  const atualizar = useCallback(async (parcial: Tema) => {
    const r = await api.put<Tema>('/api/configuracoes/cores', parcial);
    const merged = { ...TEMA_PADRAO, ...r } as Record<ChaveTema, string>;
    aplicarLegado(merged);
    setTema(merged);
    setTemplateAtivoId('personalizado');
    setModo('claro');
    gravarPref('personalizado');
    return merged;
  }, []);

  const preview = useCallback((parcial: Tema) => {
    aplicarTemaNoRoot({ ...tema, ...parcial });
  }, [tema]);

  const resetPreview = useCallback(() => {
    aplicarTemaNoRoot(tema);
  }, [tema]);

  return (
    <ThemeCtx.Provider value={{
      tema, carregado, templateAtivoId, modo, templates: TEMPLATES,
      escolherTemplate, alternarModo, definirPadraoCasa, definirTemaDaLoja,
      atualizar, preview, resetPreview,
    }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme fora de ThemeProvider');
  return ctx;
}
